/* colors */
const colorPrimary = '#f9e267';
const colorTransparent = 'transparent';

/**
 * RangeSlider class.
 *
 * To access instance of RangeSlider you can use document.querySElector('.js-range-slider').rangeSlider.value
 *
 * @example
 * const rangeSlider = document.querySelector('.js-range-slider').rangeSlider;
 * rangeSlider.addEventListener((value) => {
 *  console.log(value);
 * });
 */
class RangeSlider {

  #dom = {}

  /**
   * Listeners for the range slider.
   * @type {Array<Function>}
   */
  #listeners = [];

  #element;

  /**
   * Constructor.
   * @param {HTMLElement} element element to bind logic into
   */

  constructor(element) {
    this.#element = element;

    this.#findElements();
    this.#setRangeValues();
    this.#bindEvents();
    this.#fillRangeColor()

    this.#element.rangeSlider = this;
  }

  /**
   * Get the value of the range slider.
   * @returns {Object} object containing min and max values
   */
  get value() {
    return {
      min: parseFloat(this.#dom.minInput.value),
      max: parseFloat(this.#dom.maxInput.value)
    }
  }

  #findElements() {
    this.#dom = {
      minSlider: this.#element.querySelector('input[type="range"].min_slider'),
      maxSlider: this.#element.querySelector('input[type="range"].max_slider'),
      minInputContainer: this.#element.querySelector('.range-slider__min-input'),
      maxInputContainer: this.#element.querySelector('.range-slider__max-input'),
      minInput: this.#element.querySelector('input[type="number"].min_input'),
      maxInput: this.#element.querySelector('input[type="number"].max_input'),
      errorText: this.#element.querySelector('.range-slider__error-text'),
    }
  }

  #setRangeValues() {
    this.#dom.rangeMin = parseInt(this.#dom.minSlider.min);
    this.#dom.rangeMax = parseInt(this.#dom.maxSlider.max);
    this.#dom.minValue = parseInt(this.#dom.minSlider.value);
    this.#dom.maxValue = parseInt(this.#dom.maxSlider.value);
    this.#dom.rangeDistance = this.#dom.maxSlider.max - this.#dom.minSlider.min;
  }

  #bindEvents() {
    this.#dom.maxSlider.addEventListener('input', this.#controlMaxSlider.bind(this) );
    this.#dom.minSlider.addEventListener('input', this.#controlMinSlider.bind(this) );
    this.#dom.minInput.addEventListener('input', this.#controlMinInput.bind(this) );
    this.#dom.maxInput.addEventListener('input', this.#controlMaxInput.bind(this) );
  }

  /**
   * Control the min slider
   */
  #controlMinSlider() {
    this.#resetErrors();
    this.#dom.minValue = parseInt(this.#dom.minSlider.value);
    this.#dom.minInput.value = this.#dom.minValue;

    // If user has emptied the input field and then moves the slider,
    // the suffix of the input field should be removed
    if (this.#dom.minInput.value && this.#dom.minInputContainer.classList.contains('form-v10__toplabel-suffix')) {
      this.#dom.minInputContainer.classList.remove('form-v10__toplabel-suffix');
    }

    this.#adjustMaxSlider();
    this.#fillRangeColor()
    this.#fireEvent();
  }

  /**
   * Control the max slider
   */
  #controlMaxSlider () {
    this.#resetErrors();

    this.#dom.maxValue = parseInt(this.#dom.maxSlider.value);
    this.#dom.maxInput.value = this.#dom.maxValue;

    // If user has emptied the input field and then moves the slider,
    // the suffix of the input field should be removed
    if (this.#dom.maxInput.value && this.#dom.maxInputContainer.classList.contains('form-v10__toplabel-suffix')) {
      this.#dom.maxInputContainer.classList.remove('form-v10__toplabel-suffix');
    }

    this.#adjustMinSlider();
    this.#fillRangeColor();
    this.#fireEvent();
  }

  /**
   * Control the min input
   */
  #controlMinInput() {
    this.#resetErrors();
    if (!this.#foundErrors() && this.#dom.minInput.value) {
      this.#dom.minSlider.value = this.#dom.minInput.value;
      this.#dom.minValue = parseInt(this.#dom.minInput.value);
      this.#checkMinAgainstMax();
    }

    this.#fillRangeColor()
  }

  /**
   * Control the max input
   */
  #controlMaxInput() {
    this.#resetErrors();
    if (!this.#foundErrors() && this.#dom.maxInput.value) {
      this.#dom.maxSlider.value = this.#dom.maxInput.value;
      this.#dom.maxValue = parseInt(this.#dom.maxInput.value);
      this.#checkMaxAgainstMin();
    }

    this.#fillRangeColor()
  }

  /**
   * Adjust the min slider. If max slider is dragged over min slider,
   * min slider should follow the max slider.
   */
  #adjustMinSlider() {
    if (this.#dom.maxValue < this.#dom.minValue) {
      this.#dom.minValue = this.#dom.maxValue;
      this.#dom.minSlider.value = this.#dom.minValue;
      this.#dom.minInput.value = this.#dom.minValue;
    }
  }

  /**
   * Adjust the max slider. If min slider is dragged over max slider,
   * max slider should follow the min slider.
   */
  #adjustMaxSlider() {
    if (this.#dom.minValue > this.#dom.maxValue) {
      this.#dom.maxValue = this.#dom.minValue;
      this.#dom.maxSlider.value = this.#dom.maxValue;
      this.#dom.maxInput.value = this.#dom.maxValue;
    }
  }

  /**
   * Check min input value against max input value.
   * If min value is greater than max value, set max value to min value.
   */
  #checkMinAgainstMax() {
    if (this.#dom.minValue > this.#dom.maxValue) {
      this.#dom.maxValue = this.#dom.minValue;
      this.#dom.maxSlider.value = this.#dom.maxValue;
      this.#dom.maxInput.value = this.#dom.maxValue;
    }
  }

  /**
   * Check max input value against min input value.
   * If max value is less than min value, set min value to max value.
   */
  #checkMaxAgainstMin() {
    if (this.#dom.maxValue < this.#dom.minValue) {
      this.#dom.minValue = this.#dom.maxValue;
      this.#dom.minSlider.value = this.#dom.minValue;
      this.#dom.minInput.value = this.#dom.minValue;
    }
  }

  /**
   * Check if input fields have values outside the range or fields are empty.
   *
   * @returns {boolean} true if there are errors, false otherwise
   */
  #foundErrors() {
    const errorText = `Syötä kenttään arvo väliltä ${this.#dom.rangeMin} - ${this.#dom.rangeMax}`;

    let foundErrors = false;

    if (!this.#dom.minInput.value ||
        parseInt(this.#dom.minInput.value) < this.#dom.rangeMin ||
        parseInt(this.#dom.minInput.value) > this.#dom.rangeMax) {
      this.#dom.minInput.classList.add('error');
      this.#dom.minInput.setAttribute('aria-invalid', 'true');
      this.#dom.errorText.innerHTML = errorText;
      foundErrors = true;
    }

    if (!this.#dom.maxInput.value ||
        parseInt(this.#dom.maxInput.value) < this.#dom.rangeMin ||
        parseInt(this.#dom.maxInput.value) > this.#dom.rangeMax) {
      this.#dom.maxInput.classList.add('error');
      this.#dom.maxInput.setAttribute('aria-invalid', 'true');
      this.#dom.errorText.innerHTML = errorText;
      foundErrors = true;
    }

    return foundErrors;
  }

  /**
   * Fill the range color between min and max values.
   */
  #fillRangeColor() {
    const percent1 = ((this.#dom.minValue - this.#dom.rangeMin) / this.#dom.rangeDistance) * 100;
    const percent2 = ((this.#dom.maxValue - this.#dom.rangeMin) / this.#dom.rangeDistance) * 100;

    this.#dom.maxSlider.style.background = `linear-gradient(
      to right,
      ${colorTransparent} 0%,
      ${colorTransparent} ${percent1}%,
      ${colorPrimary} ${percent1}% ${percent2}%,
      ${colorTransparent} ${percent2}%,
      ${colorTransparent} 100%
    )`;
  }

  /**
   * Reset errors and error text.
   */
  #resetErrors() {
    this.#dom.errorText.innerHTML = '';
    this.#dom.minInput.classList.remove('error');
    this.#dom.maxInput.classList.remove('error');
    this.#dom.minInput.removeAttribute('aria-invalid');
    this.#dom.maxInput.removeAttribute('aria-invalid');
  }

  /**
   * Fire values changed even and inform listeners.
   */
  #fireEvent() {
    this.#listeners.forEach(listener => {
      listener(this.value);
    });
  }

  /**
   * Add event listener.
   * @param {function} listener listener function
   */
  addChangeListener(listener) {
    this.#listeners.push(listener);
  }

  /**
   * Remove event listener.
   * @param {function} listener listener function
   */
  removeChangeListener(listener) {
    this.#listeners = this.#listeners.filter(l => l !== listener);
  }

  /**
   * Init Static initializer of the class.
   */
  static init() {
    const rangeSliders = document.querySelectorAll('.js-range-slider');
    rangeSliders.forEach((element) => {
      new RangeSlider(element);
    });
  }
}

export default RangeSlider;
